import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import InterestThreadDrawer from "@/components/InterestThreadDrawer";
import {
  Film, Search, MapPin, Loader2, ChevronLeft, Send, Calendar, Heart,
  Check, X, MessageSquare,
} from "lucide-react";

const WINDOW_LABELS = {
  theatrical:     "Theatrical",
  home_video:     "Home Video",
  satellite_ott:  "Satellite · OTT",
  overseas:       "Overseas",
  music:          "Music",
  non_theatrical: "Non-Theatrical",
};

/**
 * Public slate discovery — any authenticated user can search films
 * every distributor has publicly listed. Filters: title/producer/language
 * substring, window kind, territory substring.
 *
 * Used by:
 *   · Exhibitors  → find films they can book
 *   · Sales agents → find distributors already carrying films in
 *                    territories they don't yet cover
 *   · Producers   → see who's carrying films in their genre
 */
export default function SlateDiscovery() {
  const [films, setFilms]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [q, setQ]                 = useState("");
  const [windowKind, setWindowKind] = useState("");
  const [territory, setTerritory] = useState("");
  const [me, setMe]               = useState(null);
  const [interested, setInterested] = useState(new Map());  // film_id -> interest row

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => setMe(data)).catch(() => setMe(null));
    api.get("/slate/interest/mine")
      .then(({ data }) => setInterested(new Map((data.interests || []).map((i) => [i.film_id, i]))))
      .catch(() => setInterested(new Map()));
  }, []);
  const isExhibitor = me?.account_type === "exhibitor_admin";

  const run = () => {
    setLoading(true);
    const params = {};
    if (q.trim())         params.q = q.trim();
    if (windowKind)       params.window_kind = windowKind;
    if (territory.trim()) params.territory = territory.trim();
    api.get("/distributor/discovery", { params })
      .then(({ data }) => setFilms(data.films || []))
      .catch(() => setFilms([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { run(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10"
         data-testid="slate-discovery-page">
      <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-white/55 hover:text-white transition"
            data-testid="slate-discovery-back">
        <ChevronLeft size={13}/> Back to Dashboard
      </Link>

      <div className="mt-6">
        <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">
          Cross-Distributor Discovery
        </div>
        <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-2 text-teal-300"
            data-testid="slate-discovery-heading">
          Licensable Films
        </h1>
        <p className="mt-3 text-sm text-white/60 max-w-2xl leading-relaxed">
          Every film every distributor on Call Sheet has publicly listed.
          Filter by window, territory or free-text — book a title,
          license a window, or scope competition.
        </p>
      </div>

      {/* Filter bar */}
      <div className="mt-8 cs-card rounded-2xl p-4 grid sm:grid-cols-4 gap-3"
           data-testid="slate-discovery-filters">
        <div className="sm:col-span-2 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"/>
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Film title, producer or language…"
                 className="bg-black/40 border-white/15 pl-9 h-10"
                 data-testid="slate-discovery-q"/>
        </div>
        <select value={windowKind} onChange={(e) => setWindowKind(e.target.value)}
                className="h-10 px-3 rounded-md bg-black/40 border border-white/15 text-sm text-white"
                data-testid="slate-discovery-window">
          <option value="">All windows</option>
          {Object.entries(WINDOW_LABELS).map(([k, l]) => (
            <option key={k} value={k}>{l}</option>
          ))}
        </select>
        <div className="flex gap-2">
          <Input value={territory} onChange={(e) => setTerritory(e.target.value)}
                 placeholder="Territory…"
                 className="bg-black/40 border-white/15 h-10 flex-1"
                 data-testid="slate-discovery-territory"/>
          <Button onClick={run}
                  className="h-10 rounded-full text-[11px] uppercase tracking-[0.22em] bg-teal-600 text-white hover:bg-teal-500"
                  data-testid="slate-discovery-run">
            Search
          </Button>
        </div>
      </div>

      {/* Results */}
      <div className="mt-8">
        {loading ? (
          <div className="cs-card rounded-xl p-8 text-center text-white/55"
               data-testid="slate-discovery-loading">
            <Loader2 size={16} className="animate-spin inline mr-2"/> Searching…
          </div>
        ) : films.length === 0 ? (
          <div className="cs-card rounded-xl p-10 text-center"
               data-testid="slate-discovery-empty">
            <Film size={30} className="mx-auto text-white/25 mb-4"/>
            <div className="text-white/70 text-sm">No films match your filter.</div>
            <div className="text-white/45 text-[11px] mt-2 max-w-md mx-auto">
              Try widening — clear the window filter or the territory search.
              Only films that distributors have publicly listed show here.
            </div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
               data-testid="slate-discovery-list">
            {films.map((f) => (
              <FilmCard key={f.id} film={f} isExhibitor={isExhibitor}
                        interest={interested.get(f.id)}
                        markInterested={(row) => setInterested((m) => {
                          const next = new Map(m);
                          next.set(f.id, row);
                          return next;
                        })}/>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FilmCard({ film, isExhibitor, interest, markInterested }) {
  const nWindows = (film.windows || []).length;
  const [showModal, setShowModal] = useState(false);
  const [showThread, setShowThread] = useState(false);
  return (
    <div className="cs-card rounded-xl p-4"
         data-testid={`slate-discovery-film-${film.id}`}>
      <div className="flex items-start gap-3">
        {film.poster_url ? (
          <img src={film.poster_url} alt={film.title}
               className="w-14 h-18 rounded-md object-cover border border-white/10 shrink-0"/>
        ) : (
          <div className="w-14 h-18 rounded-md bg-teal-500/15 text-teal-300 flex items-center justify-center shrink-0">
            <Film size={20}/>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-display text-base uppercase tracking-tight truncate">{film.title}</div>
          <div className="mt-0.5 text-[11px] text-white/50 truncate">
            {[film.year, film.language].filter(Boolean).join(" · ")}
          </div>
          {film.producer && (
            <div className="mt-1 text-[11px] text-white/60 truncate">
              Producer · {film.producer}
            </div>
          )}
        </div>
      </div>

      <div className="mt-3 pt-3 border-t border-white/8 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <Send size={11} className="text-teal-300 shrink-0"/>
          <span className="text-[11px] text-white/70 truncate">
            {film.distributor_name || "Distributor"}
          </span>
        </div>
        <span className="text-[10px] uppercase tracking-[0.18em] text-white/50 shrink-0">
          {nWindows} window{nWindows === 1 ? "" : "s"}
        </span>
      </div>

      {nWindows > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5"
             data-testid={`slate-discovery-windows-${film.id}`}>
          {film.windows.slice(0, 4).map((w, i) => (
            <span key={i}
                  className="px-1.5 py-0.5 rounded-full bg-teal-500/15 text-teal-200 text-[9px] tracking-[0.14em] uppercase inline-flex items-center gap-1">
              {WINDOW_LABELS[w.kind] || w.kind}
              <span className="text-white/40">·</span>
              <MapPin size={8}/> {w.territory}
              {(w.start_date || w.end_date) && (
                <>
                  <span className="text-white/40">·</span>
                  <Calendar size={8}/> {w.start_date?.slice(0, 7) || "?"}
                </>
              )}
            </span>
          ))}
          {nWindows > 4 && (
            <span className="text-[9px] text-white/45 tracking-[0.14em] uppercase">
              +{nWindows - 4} more
            </span>
          )}
        </div>
      )}

      {/* Want-to-book CTA — exhibitors only */}
      {isExhibitor && (
        <div className="mt-3 pt-3 border-t border-white/6">
          {interest ? (
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1.5 text-emerald-300 text-[11px] tracking-[0.18em] uppercase"
                   data-testid={`slate-discovery-interested-${film.id}`}>
                <Check size={12}/> Interest raised
              </div>
              <button onClick={() => setShowThread(true)}
                      className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-teal-500/20 text-teal-100 text-[10px] tracking-[0.18em] uppercase hover:bg-teal-500/30 transition"
                      data-testid={`slate-discovery-thread-${film.id}`}>
                <MessageSquare size={11}/> Thread
              </button>
            </div>
          ) : (
            <button onClick={() => setShowModal(true)}
                    className="w-full h-9 rounded-full text-[11px] tracking-[0.22em] uppercase bg-teal-500/20 text-teal-100 hover:bg-teal-500/30 transition inline-flex items-center justify-center gap-1.5"
                    data-testid={`slate-discovery-book-${film.id}`}>
              <Heart size={12}/> Want to Book
            </button>
          )}
        </div>
      )}

      {showModal && (
        <InterestModal
          film={film}
          onClose={() => setShowModal(false)}
          onDone={(row) => { setShowModal(false); markInterested(row); }}
        />
      )}
      {showThread && interest && (
        <InterestThreadDrawer
          interest={interest}
          myRole="exhibitor"
          onClose={() => setShowThread(false)}
        />
      )}
    </div>
  );
}

function InterestModal({ film, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [territory, setTerritory] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/slate/interest", {
        film_id: film.id, territory, note,
      });
      // Server returns only {ok, interest_id, renewed}; refetch the row.
      let row;
      try {
        const list = await api.get("/slate/interest/mine");
        row = (list.data.interests || []).find((i) => i.id === data.interest_id);
      } catch { /* fine */ }
      toast.success("Distributor notified.");
      onDone(row || {
        id: data.interest_id, film_id: film.id, film_title: film.title,
        distributor_name: film.distributor_name || "",
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not raise interest.");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
         onClick={onClose} data-testid="slate-discovery-interest-modal">
      <div className="w-full max-w-md cs-card rounded-2xl p-6"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-teal-500/15 text-teal-300 flex items-center justify-center">
              <Heart size={16}/>
            </div>
            <div>
              <div className="font-display text-base uppercase tracking-tight">Want to Book</div>
              <div className="text-[11px] text-white/55 truncate">{film.title}</div>
            </div>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white p-1"><X size={16}/></button>
        </div>
        <div className="grid gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/55 mb-1.5">Territory (optional)</div>
            <Input value={territory} onChange={(e) => setTerritory(e.target.value)}
                   placeholder="Auto: your registered state · override here…"
                   className="bg-black/40 border-white/15"
                   data-testid="slate-discovery-interest-territory"/>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/55 mb-1.5">Note to distributor (optional)</div>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)}
                      placeholder="e.g. 20 screens across UP - can open 15 Aug"
                      className="bg-black/40 border-white/15 min-h-[80px]"
                      data-testid="slate-discovery-interest-note"/>
          </div>
        </div>
        <div className="mt-5 flex gap-3">
          <Button variant="ghost" onClick={onClose}
                  className="h-10 rounded-full text-[11px] uppercase tracking-[0.22em] text-white/70 hover:bg-white/5">
            Cancel
          </Button>
          <Button className="flex-1 h-10 rounded-full text-[11px] uppercase tracking-[0.22em] bg-teal-600 text-white hover:bg-teal-500 disabled:opacity-50"
                  disabled={busy} onClick={submit}
                  data-testid="slate-discovery-interest-submit">
            {busy ? <><Loader2 size={13} className="animate-spin mr-2"/> Sending…</> : "Raise Interest"}
          </Button>
        </div>
      </div>
    </div>
  );
}
